import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus, Search, BookOpen, Loader2, Tag, ArrowUpDown, ChevronUp, ChevronDown, Filter, Trash2 } from "lucide-react";
import usePersistentState, { usePersistentSet } from "@/hooks/usePersistentState";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LearningExperienceList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = usePersistentState("lexps:filter", "all");
  const [search, setSearch] = usePersistentState("lexps:search", "");
  const [sortField, setSortField] = useState("title");
  const [sortDir, setSortDir] = useState("asc");
  const [trackFilter, setTrackFilter] = usePersistentSet("lexps:trackFilter", []); // selected track IDs or "__unassigned__"
  const [trackFilterOpen, setTrackFilterOpen] = useState(false);
  const trackFilterRef = useRef(null);

  const fetchItems = useCallback(async () => {
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/learning-experiences`, { params, withCredentials: true });
      setItems(data);
    } catch (err) {
      console.error("Failed to load learning experiences:", err);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const t = setTimeout(fetchItems, 200);
    return () => clearTimeout(t);
  }, [fetchItems]);

  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (item, e) => {
    // Stop the row click-through so we don't navigate into the LEXP we're
    // trying to delete.
    e.stopPropagation();
    const trackCount = (item.assigned_tracks || []).length;
    const trackMsg = trackCount
      ? `\n\nThis experience is assigned to ${trackCount} track${trackCount === 1 ? "" : "s"}. It will be removed from them.`
      : "";
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.${trackMsg}`)) return;
    setDeletingId(item.id);
    try {
      await axios.delete(`${API}/learning-experiences/${item.id}`, { withCredentials: true });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Learning experience deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete learning experience");
    } finally {
      setDeletingId(null);
    }
  };

  const filterTabs = [
    { value: "all", label: "All" },
    { value: "published", label: "Published" },
    { value: "draft", label: "Draft" },
  ];

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const sorted = [...items].sort((a, b) => {
    let va, vb;
    if (sortField === "title") { va = a.title || ""; vb = b.title || ""; }
    else if (sortField === "status") { va = a.status || ""; vb = b.status || ""; }
    else if (sortField === "items") { va = a.item_count || 0; vb = b.item_count || 0; }
    else if (sortField === "tracks") { va = (a.assigned_tracks || []).length; vb = (b.assigned_tracks || []).length; }
    else if (sortField === "tags") { va = (a.meta_tags || []).length; vb = (b.meta_tags || []).length; }
    else { va = a.title || ""; vb = b.title || ""; }
    if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === "asc" ? va - vb : vb - va;
  });

  const SortIcon = ({ field }) => sortField === field
    ? (sortDir === "asc" ? <ChevronUp className="h-3 w-3 inline ml-0.5" /> : <ChevronDown className="h-3 w-3 inline ml-0.5" />)
    : <ArrowUpDown className="h-3 w-3 inline ml-0.5 opacity-30" />;

  // Derive unique tracks from all LEXPs for the filter
  const allTracks = [];
  const seenTrackIds = new Set();
  let hasUnassigned = false;
  for (const item of items) {
    if (!item.assigned_tracks || item.assigned_tracks.length === 0) { hasUnassigned = true; continue; }
    for (const t of item.assigned_tracks) {
      if (!seenTrackIds.has(t.id)) { seenTrackIds.add(t.id); allTracks.push(t); }
    }
  }
  allTracks.sort((a, b) => (a.title || "").localeCompare(b.title || ""));

  const toggleTrackFilter = (id) => {
    setTrackFilter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Close dropdown on click outside
  useEffect(() => {
    if (!trackFilterOpen) return;
    const handler = (e) => { if (trackFilterRef.current && !trackFilterRef.current.contains(e.target)) setTrackFilterOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [trackFilterOpen]);

  // Apply track filter to sorted list
  const displayed = trackFilter.size === 0 ? sorted : sorted.filter((item) => {
    const tracks = item.assigned_tracks || [];
    if (tracks.length === 0) return trackFilter.has("__unassigned__");
    return tracks.some((t) => trackFilter.has(t.id));
  });

  return (
    <div className="space-y-6" data-testid="le-list-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="le-list-title">
            Learning Experience Library
          </h2>
          <p className="text-[#6E7787] font-body text-sm mt-1">
            Build and manage learning experiences
          </p>
        </div>
        <Button
          onClick={() => navigate("/admin/learning-experiences/new")}
          data-testid="create-le-btn"
          className="h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90 shrink-0"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Create Learning Experience
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-3 items-center">
          <div className="flex gap-1 bg-[#11141A] border border-white/10 rounded-lg p-1">
            {filterTabs.map((tab) => (
              <button key={tab.value} onClick={() => setFilter(tab.value)} data-testid={`le-filter-${tab.value}`}
                className={`px-3 py-1.5 rounded-md text-sm font-body transition-all ${filter === tab.value ? "text-white bg-white/10" : "text-[#6E7787] hover:text-[#A1A9B8]"}`}>
                {tab.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title..." data-testid="le-search"
              className="pl-9 bg-[#11141A] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] h-9" />
          </div>
          {/* Track filter */}
          <div ref={trackFilterRef} className="relative">
            <button
              onClick={() => setTrackFilterOpen((p) => !p)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-body transition-colors ${trackFilter.size > 0 ? "text-white bg-white/10" : "text-[#6E7787] hover:text-[#A1A9B8] hover:bg-white/5"}`}
              style={{ border: "1px solid rgba(255,255,255,0.1)" }}
              data-testid="le-track-filter-btn"
            >
              <Filter className="h-3.5 w-3.5" />
              Track{trackFilter.size > 0 ? ` (${trackFilter.size})` : ""}
            </button>
            {trackFilterOpen && (
              <div className="absolute left-0 top-full mt-1 z-50 min-w-[280px] max-w-md rounded-lg border shadow-lg overflow-hidden"
                style={{ backgroundColor: "#fff", borderColor: "#e2e6ef" }}
                data-testid="le-track-filter-dropdown"
              >
                <div className="max-h-56 overflow-y-auto p-1.5">
                  {hasUnassigned && (
                    <label className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md cursor-pointer hover:bg-black/[0.03] transition-colors">
                      <input type="checkbox" checked={trackFilter.has("__unassigned__")} onChange={() => toggleTrackFilter("__unassigned__")}
                        className="rounded border-[#d1d5de] bg-transparent text-[var(--primary-color)] focus:ring-0 h-3.5 w-3.5 shrink-0" />
                      <span className="text-xs font-body italic" style={{ color: "#6E7787" }}>Unassigned</span>
                    </label>
                  )}
                  {allTracks.map((t) => (
                    <label key={t.id} className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md cursor-pointer hover:bg-black/[0.03] transition-colors">
                      <input type="checkbox" checked={trackFilter.has(t.id)} onChange={() => toggleTrackFilter(t.id)}
                        className="rounded border-[#d1d5de] bg-transparent text-[var(--primary-color)] focus:ring-0 h-3.5 w-3.5 shrink-0" />
                      <span className="text-xs font-body" style={{ color: "#1e293b" }}>{t.title}</span>
                    </label>
                  ))}
                  {allTracks.length === 0 && !hasUnassigned && (
                    <p className="text-xs font-body px-2.5 py-3 text-center" style={{ color: "#6E7787" }}>No tracks available</p>
                  )}
                </div>
                {trackFilter.size > 0 && (
                  <div className="border-t px-2.5 py-2" style={{ borderColor: "#e2e6ef" }}>
                    <button onClick={() => setTrackFilter(new Set())} className="text-xs font-body font-medium w-full text-left hover:underline"
                      style={{ color: "var(--primary-color)" }} data-testid="le-track-filter-clear">
                      Clear all
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* View toggle removed — inline only */}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center" data-testid="le-empty-state">
          <div className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 10%, transparent)" }}>
            <BookOpen className="h-7 w-7" style={{ color: "var(--primary-color, #4A90D9)" }} />
          </div>
          <h3 className="font-body text-lg font-bold text-white mb-2">
            {search ? "No matching experiences" : "No learning experiences yet"}
          </h3>
          <p className="text-[#6E7787] font-body text-sm max-w-sm mx-auto">
            {search ? "Try adjusting your search or filter." : "Create your first learning experience to get started."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-[#11141A] border border-white/10" data-testid="le-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort("title")}>
                  Title <SortIcon field="title" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort("status")}>
                  Status <SortIcon field="status" />
                </th>
                <th className="text-right px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort("items")}>
                  Items <SortIcon field="items" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort("tracks")}>
                  Assigned Tracks <SortIcon field="tracks" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider cursor-pointer select-none" onClick={() => toggleSort("tags")}>
                  Meta Tags <SortIcon field="tags" />
                </th>
                <th className="w-12 px-2 py-3" aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((item) => (
                <tr key={item.id} onClick={() => navigate(`/admin/learning-experiences/${item.id}`)}
                  className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer transition-colors"
                  data-testid={`le-row-${item.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-body font-medium text-white">{item.title}</span>
                      </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={item.status === "published" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" : "bg-amber-500/15 text-amber-400 border-amber-500/20"}>
                      {item.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-body text-[#A1A9B8]">{item.item_count || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(item.assigned_tracks || []).length === 0
                        ? <span className="text-xs text-[#6E7787] font-body">—</span>
                        : item.assigned_tracks.map((t) => (
                          <span key={t.id} className="text-xs font-body bg-white/5 text-[#A1A9B8] rounded px-1.5 py-0.5">{t.title}</span>
                        ))
                      }
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(item.meta_tags || []).length === 0
                        ? <span className="text-xs text-[#6E7787] font-body">—</span>
                        : item.meta_tags.map((t) => (
                          <span key={t.id} className="text-xs font-body rounded px-1.5 py-0.5" style={{ backgroundColor: "color-mix(in srgb, var(--primary-color) 15%, transparent)", color: "var(--primary-color)" }}>{t.name}</span>
                        ))
                      }
                    </div>
                  </td>
                  <td className="w-12 px-2 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(item, e)}
                      disabled={deletingId === item.id}
                      title="Delete learning experience"
                      aria-label={`Delete ${item.title}`}
                      data-testid={`le-delete-${item.id}`}
                      className="p-1.5 rounded-md text-[#6E7787] hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {deletingId === item.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
